import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Paper, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { sortBy } from 'lodash';

const styles = theme => ({
  root: {
    width: '100%',
    display: 'flex',
    alignContent: 'center',
    padding: theme.spacing(1),
    flexDirection: 'column',

  },
  header: {
    display: 'flex',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(1),
  },
  item: {
    margin: theme.spacing(0.25),
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.facet.lightGray}`,
  },
  subitem: {
    margin: theme.spacing(0.25),
    display: 'flex',
    paddingLeft: theme.spacing(2),
    '&:nth-child(odd)': {
      backgroundColor: theme.palette.facet.lightGray,
    },
  },
  section: {
    display: 'flex',
    flexGrow: 1,
    alignContent: 'center',
    flexDirection: 'column',
  },
  name: {
    flexGrow: 2,
  },
  percentage: {
    width: 64,
  },
});


const useBreakdown = (portfolio, recommended) => {
  const tree = {};
  if (!portfolio || !recommended) return tree;
  // Create categories from the recommended portfolio
  for (const d of recommended.attr('assetClassChartData')) {
    tree[d.id()] = {
      value: 0,
      recommendedValue: parseFloat(d.attr('percentageValue')),
      subCategories: {},
    };
  }
  // Create subcategories
  for (const d of recommended.attr('categoryChartData')) {
    if (tree[d.attr('type')]) {
      tree[d.attr('type')].subCategories[d.attr('label')] = {
        value: 0,
        recommendedValue: parseFloat(d.attr('percentageValue')),
      };
    }
    // else mark unclassifed?
  }

  // Populate categories from the portfolio
  for (const d of portfolio.attr('assetClassChartData')) {
    if (tree[d.id()]) {
      tree[d.id()].value = parseFloat(d.attr('percentageValue'));
    }
  }
  // Populate subcategories
  for (const d of portfolio.attr('categoryChartData')) {
    if (tree[d.attr('type')] && tree[d.attr('type')].subCategories[d.attr('label')]) {
      tree[d.attr('type')].subCategories[d.attr('label')].value = parseFloat(
        d.attr('percentageValue'),
      );
    }
    // else mark unclassifed?
  }

  return tree;
};

function PortfolioComparisonBreakdown({
  portfolio, classes, className, selectedPortfolioName, loading,
}) {
  const selectedPortfolio = portfolio?.attr('recommended').resources().list().find(r => r.attr('name') === selectedPortfolioName);
  const breakdown = useBreakdown(portfolio, selectedPortfolio);
  const Breakdown = loading ? (
    <>{Array(10).fill(<Skeleton animation="wave" height={32} />)}</>
  ) : sortBy(Object.entries(breakdown), ([n]) => n).map(([name, data]) => {
    const difference = Math.abs(data.recommendedValue - data.value).toFixed(2);
    const color = difference >= 5 ? 'error' : 'primary';
    return (
      <div className={classes.section}>
        <div className={classes.item}>
          <Typography className={classes.name} color={color}>
            {name}
          </Typography>
          <Typography className={classes.percentage} color={color}>
            {data.value}%
          </Typography>
          <Typography className={classes.percentage} color={color}>
            {data.recommendedValue}%
          </Typography>
          <Typography className={classes.percentage} color={color}>
            {difference}%
          </Typography>
        </div>
        {sortBy(Object.entries(data.subCategories), ([n]) => n).map(([subName, subData]) => {
          const subDifference = Math.abs(subData.recommendedValue - subData.value).toFixed(2);
          const subColor = subDifference >= 5 ? 'error' : 'primary';

          return (
            <div className={classes.subitem}>
              <Typography className={classes.name} color={subColor} noWrap>
                {subName}
              </Typography>
              <Typography className={classes.percentage} color={subColor} noWrap>
                {subData.value}%
              </Typography>
              <Typography className={classes.percentage} color={subColor} noWrap>
                {subData.recommendedValue}%
              </Typography>
              <Typography className={classes.percentage} color={subColor} noWrap>
                {subDifference}%
              </Typography>
            </div>
          );
        })}
      </div>
    );
  });

  return (
    <Paper square className={classNames(classes.root, className)}>
      <div className={classes.header}>
        <Typography color="primary" className={classes.name}>
          Breakdown
        </Typography>
        <Typography color="primary" className={classes.percentage}>
          Current
        </Typography>
        <Typography color="primary" className={classes.percentage}>
          Facet
        </Typography>
        <Typography color="primary" className={classes.percentage}>
          Difference
        </Typography>
      </div>
      {Breakdown}
    </Paper>
  );
}

PortfolioComparisonBreakdown.defaultProps = {
  className: '',
};

PortfolioComparisonBreakdown.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedPortfolioName: PropTypes.string.isRequired,
  portfolio: PropTypes.object.isRequired,
};

export default withStyles(styles)(PortfolioComparisonBreakdown);
